import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Building2, RefreshCw, Loader2, Mail, Phone, Users, Link2, ExternalLink, AlertTriangle, CheckCircle2, Info, MoreHorizontal, KeyRound, ArrowUpCircle, ArrowDownCircle, XCircle, Receipt, Search, Gift, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { RoleBadge } from './RoleBadge';
import { AdminDateFilter } from './AdminDateFilter';
import { EnterpriseEnquiriesInbox } from './EnterpriseEnquiriesInbox';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const t = localStorage.getItem('le_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
};

const fmtEpoch = (secs) => {
  if (!secs) return '—';
  try {
    return new Date(secs * 1000).toLocaleString();
  } catch {
    return String(secs);
  }
};

const DetailRow = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 items-start text-[13px]">
    <span className="text-muted-foreground">{label}</span>
    <span
      className={`${mono ? 'font-mono text-[12px]' : ''} break-all text-foreground`}
      data-testid={`admin-detail-value-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
    >
      {value || <span className="text-muted-foreground">—</span>}
    </span>
  </div>
);

/**
 * BillingProvisionsPanel — admin billing + CRM surface. Bundles:
 * 1. Recent Razorpay hosted-button provisions (Full Profile Makeover)
 * 2. Recent Razorpay payments (raw feed) + admin sub-tools
 * 3. Sales inbox — Quarterly + Enterprise lead enquiries (tabbed)
 *
 * All admin-only endpoints. The sales inbox is delegated to
 * <EnterpriseEnquiriesInbox /> so this file stays focused on billing.
 */
export const BillingProvisionsPanel = () => {
  const [provisions, setProvisions] = useState([]);
  const [paidUsers, setPaidUsers] = useState([]);
  const [rzpPayments, setRzpPayments] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingPU, setLoadingPU] = useState(false);
  const [loadingRZP, setLoadingRZP] = useState(false);
  const [attachingId, setAttachingId] = useState(null); // payment_id being attached
  const [attachUidByPid, setAttachUidByPid] = useState({}); // draft user-id text per row
  const [detailsRow, setDetailsRow] = useState(null); // customer whose full details are open in a dialog
  const [scorePreviews, setScorePreviews] = useState([]);
  const [loadingSP, setLoadingSP] = useState(false);
  // "Free score submissions" is legacy funnel data — collapse by default
  // so it doesn't dominate the admin home. Admin can toggle open on demand.
  const [scoreSubsOpen, setScoreSubsOpen] = useState(false);
  const [regUsers, setRegUsers] = useState([]);
  const [loadingRU, setLoadingRU] = useState(false);
  const [regUsersQ, setRegUsersQ] = useState('');
  const [actionUserId, setActionUserId] = useState(null); // user currently being acted on
  const [payFunnel, setPayFunnel] = useState({ counts: {}, items: [] });
  const [loadingPF, setLoadingPF] = useState(false);
  const [payFunnelDateRange, setPayFunnelDateRange] = useState({ from: '', to: '' });
  const [grantCompOpen, setGrantCompOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ email: '', name: '', send_credentials: true });
  const [grantBusy, setGrantBusy] = useState(false);
  // Admin-reset result surface — shows the freshly minted temp password
  // in a dialog with a copy button so admin can hand it over safely.
  const [resetResult, setResetResult] = useState(null); // { email, temp_password }

  const loadProvisions = async () => {
    setLoadingP(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/hosted-payment-provisions`, {
        headers: authHeaders(),
        timeout: 20000,
      });
      setProvisions(res?.data?.items || []);
    } catch (err) {
      toast.error('Could not load hosted-button provisions.');
    } finally {
      setLoadingP(false);
    }
  };

  const loadPaidUsers = async () => {
    setLoadingPU(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/paid-users`, {
        headers: authHeaders(),
        timeout: 20000,
      });
      setPaidUsers(res?.data?.items || []);
    } catch (err) {
      toast.error('Could not load paid users.');
    } finally {
      setLoadingPU(false);
    }
  };

  const loadRzpPayments = async () => {
    setLoadingRZP(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/razorpay/payments?count=25`, {
        headers: authHeaders(),
        timeout: 30000,
      });
      setRzpPayments(res?.data?.items || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not fetch payments from Razorpay.';
      toast.error(typeof msg === 'string' ? msg : 'Could not fetch payments from Razorpay.');
    } finally {
      setLoadingRZP(false);
    }
  };

  const attachPaymentTo = async (payment_id, target_user_id) => {
    if (!target_user_id || target_user_id.length < 8) {
      toast.error('Enter a valid target user id.');
      return;
    }
    if (!window.confirm(`Attach payment ${payment_id} to user ${target_user_id}? This will activate their one-time plan.`)) return;
    setAttachingId(payment_id);
    try {
      const res = await axios.post(
        `${BACKEND}/api/admin/paid-users/attach-payment`,
        null,
        {
          headers: authHeaders(),
          params: { payment_id, target_user_id },
          timeout: 25000,
        },
      );
      if (res?.data?.ok) {
        toast.success('Payment attached and plan activated.');
        loadPaidUsers();
        loadRzpPayments();
      } else {
        toast.error('Attach failed.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Attach failed.');
    } finally {
      setAttachingId(null);
    }
  };

  useEffect(() => {
    loadPaidUsers();
    loadRzpPayments();
    loadProvisions();
    loadScorePreviews();
    loadRegisteredUsers();
    loadPaymentFunnel();
  }, []);

  const loadPaymentFunnel = async (range = payFunnelDateRange) => {
    setLoadingPF(true);
    try {
      const params = { limit: 200 };
      if (range?.from) params.date_from = range.from;
      if (range?.to) params.date_to = range.to;
      const res = await axios.get(`${BACKEND}/api/admin/tracking/payment-events`, {
        headers: authHeaders(),
        params,
        timeout: 20000,
      });
      setPayFunnel({
        counts: res?.data?.counts || {},
        items: res?.data?.items || [],
        click_to_start_pct: res?.data?.click_to_start_pct,
        completion_pct: res?.data?.completion_pct,
        abandonment_pct: res?.data?.abandonment_pct,
      });
    } catch (_err) {
      toast.error('Could not load payment funnel.');
    } finally {
      setLoadingPF(false);
    }
  };

  const submitGrantComp = async () => {
    const email = grantForm.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email.');
      return;
    }
    setGrantBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/admin/grant-comp`,
        {
          email,
          name: grantForm.name.trim() || undefined,
          send_credentials: !!grantForm.send_credentials,
        },
        { headers: authHeaders() },
      );
      const d = res?.data || {};
      const verb = d.created ? 'Created + granted' : 'Reactivated as comp';
      const emailNote =
        d.email_status === 'sent'
          ? ' · credentials emailed'
          : d.email_status === 'email_service_disabled'
          ? ' · email service off — deliver credentials manually'
          : d.email_status === 'not_sent'
          ? ''
          : ` · email: ${d.email_status}`;
      toast.success(`${verb}: ${email}${emailNote}`);
      setGrantCompOpen(false);
      setGrantForm({ email: '', name: '', send_credentials: true });
      loadRegisteredUsers(regUsersQ);
      loadPaidUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Grant failed.';
      toast.error(typeof msg === 'string' ? msg : 'Grant failed.');
    } finally {
      setGrantBusy(false);
    }
  };

  const loadScorePreviews = async () => {
    setLoadingSP(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/score-previews`, {
        headers: authHeaders(),
        timeout: 20000,
      });
      setScorePreviews(res?.data?.items || []);
    } catch (err) {
      toast.error('Could not load free-score submissions.');
    } finally {
      setLoadingSP(false);
    }
  };

  const loadRegisteredUsers = async (q = '') => {
    setLoadingRU(true);
    try {
      const params = { limit: 500 };
      if (q && q.trim()) params.q = q.trim();
      const res = await axios.get(`${BACKEND}/api/admin/registered-users`, {
        headers: authHeaders(),
        params,
        timeout: 25000,
      });
      setRegUsers(res?.data?.items || []);
    } catch (err) {
      toast.error('Could not load registered users.');
    } finally {
      setLoadingRU(false);
    }
  };

  const runUserAction = async (user_id, action) => {
    // action: 'reset_password' | 'activate_one_time' | 'activate_gold' | 'activate_comp' | 'cancel' | 'send_invoice'
    const label = user_id?.substring(0, 8);
    if (!user_id) return;
    const confirmMsg = {
      reset_password: `Flag this user for a password reset?`,
      activate_one_time: `Activate Profile Success Plan (15-day validity) for ${label}?`,
      activate_gold: `Activate Executive Thought Leadership Plan (30-day billing) for ${label}?`,
      activate_comp: `Grant complimentary access to ${label}?`,
      cancel: `Cancel this user's subscription immediately?`,
      send_invoice: `Send a payment receipt/invoice email to this user?`,
    }[action];
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    setActionUserId(user_id);
    try {
      if (action === 'reset_password') {
        const r = await axios.post(`${BACKEND}/api/admin/users/${user_id}/reset-password`, null, {
          headers: authHeaders(),
        });
        const tp = r?.data?.temp_password;
        if (tp) {
          setResetResult({
            email: r.data.email,
            temp_password: tp,
          });
          toast.success(`Temp password minted for ${r.data.email}`);
        } else {
          toast.success('Password reset flagged. User will get a temp password.');
        }
      } else if (action === 'send_invoice') {
        const r = await axios.post(`${BACKEND}/api/admin/users/${user_id}/send-invoice`, null, {
          headers: authHeaders(),
        });
        toast.success(`Receipt sent to ${r?.data?.to}`);
      } else if (action === 'cancel') {
        await axios.post(
          `${BACKEND}/api/admin/subscriptions/${user_id}`,
          { plan_type: 'none', status: 'canceled', comp: false },
          { headers: authHeaders() },
        );
        toast.success('Subscription cancelled.');
      } else if (action === 'activate_one_time') {
        await axios.post(
          `${BACKEND}/api/admin/subscriptions/${user_id}`,
          { plan_type: 'one_time', status: 'active', comp: false, period_days: 15, reset_usage: true },
          { headers: authHeaders() },
        );
        toast.success('Profile Success Plan activated (15 days).');
      } else if (action === 'activate_gold') {
        await axios.post(
          `${BACKEND}/api/admin/subscriptions/${user_id}`,
          { plan_type: 'gold', status: 'active', comp: false, period_days: 30, reset_usage: true },
          { headers: authHeaders() },
        );
        toast.success('Executive Thought Leadership Plan activated (30 days).');
      } else if (action === 'activate_comp') {
        await axios.post(
          `${BACKEND}/api/admin/subscriptions/${user_id}`,
          { plan_type: 'comp', status: 'active', comp: true, reset_usage: true },
          { headers: authHeaders() },
        );
        toast.success('Complimentary access granted.');
      }
      loadRegisteredUsers(regUsersQ);
      loadPaidUsers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Action failed.';
      toast.error(typeof msg === 'string' ? msg : 'Action failed.');
    } finally {
      setActionUserId(null);
    }
  };

  return (
    <>
      {/* Customers — every user on any plan (paid or comped) */}
      <Card className="border-border" data-testid="admin-paid-users-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
            <Users size={18} className="text-primary" />
            <h2 className="font-semibold">Customers</h2>
            <Badge variant="secondary" className="ml-1" data-testid="admin-paid-users-count">
              {paidUsers.length} total
            </Badge>
            {paidUsers.some((r) => r.is_active) && (
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]" data-testid="admin-paid-users-active-count">
                {paidUsers.filter((r) => r.is_active).length} active
              </Badge>
            )}
            {paidUsers.some((r) => r.is_active === false) && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="admin-paid-users-past-count">
                {paidUsers.filter((r) => !r.is_active).length} past
              </Badge>
            )}
            <span className="text-xs text-muted-foreground hidden md:inline">
              Every user on a plan — paid, comped, or expired. Active shown first.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadPaidUsers}
              disabled={loadingPU}
              className="ml-auto"
              data-testid="admin-paid-users-refresh"
            >
              {loadingPU ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
          </div>

          {paidUsers.length === 0 && !loadingPU ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No customers yet (active or past).
            </div>
          ) : (
            <div className="max-h-[520px] overflow-auto">
              <Table className="min-w-[1180px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Renews / expires</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Referral path</TableHead>
                    <TableHead>Signup</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead className="w-[64px] text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paidUsers.map((row) => (
                    <TableRow key={row.user_id} className={row.is_active === false ? 'opacity-70' : ''}>
                      <TableCell className="font-medium whitespace-nowrap">{row.name || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <a href={`mailto:${row.email}`} className="text-primary hover:underline">
                          {row.email}
                        </a>
                        {row.needs_password_reset && (
                          <Badge variant="outline" className="ml-2 text-[10px] text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-500/10">
                            Reset pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.is_active ? (
                          <Badge className="uppercase text-[10px] tracking-wider bg-emerald-600 hover:bg-emerald-600">
                            {row.plan_type} · active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="uppercase text-[10px] tracking-wider text-muted-foreground border-muted">
                            {row.plan_type} · past ({row.status})
                          </Badge>
                        )}
                        {row.comp && (
                          <Badge variant="outline" className="ml-1.5 text-[10px]">comp</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.current_period_end)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.from_hosted_button ? (
                          <Badge variant="outline" className="text-[10px]">Hosted button</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">{row.created_via || 'signup'}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.referral_path ? (
                          <button
                            type="button"
                            onClick={() => setDetailsRow(row)}
                            className="font-mono text-[11px] text-primary hover:underline truncate max-w-[220px] inline-block align-middle"
                            title={row.referral_path}
                            data-testid={`admin-customer-referral-${row.user_id}`}
                          >
                            {row.referral_path}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.user_created_at)}</TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                        {row.user_id?.substring(0, 8)}…
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setDetailsRow(row)}
                          data-testid={`admin-customer-details-btn-${row.user_id}`}
                          aria-label="View customer details"
                        >
                          <Info size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer details dialog — full referral/tracking context */}
      <Dialog open={!!detailsRow} onOpenChange={(v) => !v && setDetailsRow(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-customer-details-dialog">
          <DialogHeader>
            <DialogTitle>{detailsRow?.name || detailsRow?.email || 'Customer'}</DialogTitle>
            <DialogDescription>
              Full acquisition + billing context for this customer.
            </DialogDescription>
          </DialogHeader>
          {detailsRow && (
            <div className="text-sm space-y-3 max-h-[520px] overflow-auto">
              <DetailRow label="Email" value={detailsRow.email} mono />
              <DetailRow label="User ID" value={detailsRow.user_id} mono />
              <DetailRow
                label="Plan"
                value={`${detailsRow.plan_type || '—'} · ${detailsRow.status || '—'}${detailsRow.comp ? ' · comp' : ''}`}
              />
              <DetailRow label="Renews / expires" value={fmtDate(detailsRow.current_period_end)} />
              <DetailRow label="Signed up" value={fmtDate(detailsRow.user_created_at)} />
              <DetailRow label="Signup source" value={detailsRow.created_via || 'signup'} />
              <div className="pt-3 border-t border-border">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
                  Referral / acquisition path
                </p>
                <DetailRow label="Internal path before payment" value={detailsRow.referral_path} mono />
                <DetailRow label="Landing page (first hit)" value={detailsRow.referral_landing_path} mono />
                <DetailRow label="External referrer" value={detailsRow.referral_source} mono />
                <DetailRow label="UTM (source / medium / campaign)" value={detailsRow.referral_utm} mono />
              </div>
              {detailsRow.hosted_payment_id && (
                <div className="pt-3 border-t border-border">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Hosted-button payment</p>
                  <DetailRow label="Razorpay payment ID" value={detailsRow.hosted_payment_id} mono />
                  <DetailRow label="Amount (INR)" value={detailsRow.hosted_amount_paise ? `₹${(detailsRow.hosted_amount_paise/100).toLocaleString('en-IN')}` : '—'} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin reset-password result — shows the newly minted temp password
          once, with a copy button. Closing the dialog forgets it forever. */}
      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent className="max-w-md" data-testid="admin-reset-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={16} className="text-primary" />
              New temporary password
            </DialogTitle>
            <DialogDescription>
              Share this with <span className="font-medium">{resetResult?.email}</span> over a secure channel. It won&apos;t be shown again after you close this dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-primary/30 bg-primary/[0.05] p-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                Temp password
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 font-mono text-base tracking-wider select-all break-all"
                  data-testid="admin-reset-result-password"
                >
                  {resetResult?.temp_password}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(resetResult?.temp_password || '');
                      toast.success('Copied to clipboard');
                    } catch (_) {
                      toast.error('Copy failed — select + copy manually.');
                    }
                  }}
                  data-testid="admin-reset-result-copy"
                >
                  Copy
                </Button>
              </div>
            </div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 text-[12px] text-foreground/80 leading-relaxed">
              The user will be prompted to set their own password on first
              login. If you&apos;d prefer the user reset it themselves, ask
              them to use the &ldquo;Forgot password?&rdquo; link on the
              login page &mdash; they&apos;ll get a self-service reset link
              by email.
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setResetResult(null)}
              data-testid="admin-reset-result-close"
            >
              I&apos;ve saved it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grant complimentary access dialog — new OR existing email */}
      <Dialog open={grantCompOpen} onOpenChange={(v) => !grantBusy && setGrantCompOpen(v)}>        <DialogContent className="max-w-md" data-testid="admin-grant-comp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift size={16} className="text-primary" />
              Grant complimentary access
            </DialogTitle>
            <DialogDescription>
              Activates unlimited comp on the given email. If the account doesn&apos;t exist yet, we&apos;ll create it and (optionally) email the temp password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground" htmlFor="grant-comp-email">
                Email
              </label>
              <Input
                id="grant-comp-email"
                type="email"
                value={grantForm.email}
                onChange={(e) => setGrantForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="name@example.com"
                className="mt-1.5"
                data-testid="admin-grant-comp-email-input"
                disabled={grantBusy}
              />
            </div>
            <div>
              <label className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground" htmlFor="grant-comp-name">
                Name <span className="opacity-60 normal-case">(optional — used only for new accounts)</span>
              </label>
              <Input
                id="grant-comp-name"
                value={grantForm.name}
                onChange={(e) => setGrantForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                className="mt-1.5"
                data-testid="admin-grant-comp-name-input"
                disabled={grantBusy}
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={grantForm.send_credentials}
                onChange={(e) => setGrantForm((f) => ({ ...f, send_credentials: e.target.checked }))}
                className="h-4 w-4 rounded border-border"
                data-testid="admin-grant-comp-send-credentials"
                disabled={grantBusy}
              />
              Email login credentials to a new account
            </label>
            <div className="rounded-lg border border-primary/20 bg-primary/[0.04] px-3 py-2.5 text-[12px] text-foreground/80 leading-relaxed">
              Comp accounts have <b>unlimited</b> audits, posts, and carousels — never expire, and can paste any LinkedIn URL for full profile scraping.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setGrantCompOpen(false)}
              disabled={grantBusy}
              data-testid="admin-grant-comp-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitGrantComp}
              disabled={grantBusy || !grantForm.email.trim()}
              data-testid="admin-grant-comp-submit"
            >
              {grantBusy ? <><Loader2 size={14} className="mr-2 animate-spin" />Granting…</> : 'Grant access'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Free LinkedIn score submissions — every URL fed into the homepage widget */}
      <Card className="border-border mt-6" data-testid="admin-score-previews-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
            <Link2 size={18} className="text-primary" />
            <h2 className="font-semibold">Free score submissions</h2>
            <Badge variant="secondary" className="ml-1" data-testid="admin-score-previews-count">
              {scorePreviews.length}
            </Badge>
            <span className="text-xs text-muted-foreground hidden md:inline">
              Every LinkedIn URL submitted to the homepage &ldquo;10-second score&rdquo; widget.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadScorePreviews}
              disabled={loadingSP}
              className="ml-auto"
              data-testid="admin-score-previews-refresh"
            >
              {loadingSP ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setScoreSubsOpen((o) => !o)}
              className="px-2"
              aria-expanded={scoreSubsOpen}
              data-testid="admin-score-previews-toggle"
            >
              {scoreSubsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Button>
          </div>

          {scoreSubsOpen && (scorePreviews.length === 0 && !loadingSP ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No free-score submissions yet.
            </div>
          ) : (
            <div className="max-h-[440px] overflow-auto">
              <Table className="min-w-[880px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead>Submitted at</TableHead>
                    <TableHead>LinkedIn URL</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Cadence</TableHead>
                    <TableHead>Est. score</TableHead>
                    <TableHead>Signed up?</TableHead>
                    <TableHead>IP / country</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scorePreviews.map((row) => (
                    <TableRow key={row.id} data-testid={`admin-score-preview-row-${row.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <a
                          href={row.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          <span className="truncate max-w-[280px] inline-block align-middle">
                            {row.linkedin_url}
                          </span>
                          <ExternalLink size={12} className="opacity-60" />
                        </a>
                      </TableCell>
                      <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">{row.role || '—'}</TableCell>
                      <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">{row.cadence || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        <span className="tabular-nums">{row.current_score ?? '—'}</span>
                        {row.potential_score != null && (
                          <span className="text-muted-foreground"> → </span>
                        )}
                        {row.potential_score != null && (
                          <span className="tabular-nums text-sky-500">{row.potential_score}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.converted_to_signup ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">Yes</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not yet</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {row.country || '—'}
                        {row.ip && (
                          <span className="ml-1.5 font-mono text-[10px] opacity-60">{row.ip}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Payment funnel — click → started → completed / abandoned / failed */}
      <Card className="border-border mt-6" data-testid="admin-payment-funnel-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
            <CreditCard size={18} className="text-primary" />
            <h2 className="font-semibold">Payment funnel</h2>
            <Badge variant="secondary" className="ml-1" data-testid="admin-payment-funnel-total">
              {Object.values(payFunnel.counts || {}).reduce((a, b) => a + b, 0)} events
            </Badge>
            {typeof payFunnel.abandonment_pct === 'number' && (
              <Badge
                variant="outline"
                className={`text-[10px] ${payFunnel.abandonment_pct > 50 ? 'text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-500/10' : 'text-muted-foreground'}`}
                data-testid="admin-payment-funnel-abandonment"
              >
                {payFunnel.abandonment_pct}% abandonment
              </Badge>
            )}
            <span className="text-xs text-muted-foreground hidden md:inline">
              Every CTA click + Razorpay lifecycle event across the app.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadPaymentFunnel(payFunnelDateRange)}
              disabled={loadingPF}
              className="ml-auto"
              data-testid="admin-payment-funnel-refresh"
            >
              {loadingPF ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
          </div>

          <div className="px-4 py-3 border-b border-border">
            <AdminDateFilter
              value={payFunnelDateRange}
              onChange={(range) => { setPayFunnelDateRange(range); loadPaymentFunnel(range); }}
              testId="admin-payment-funnel-date"
            />
          </div>

          {/* Funnel KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-border">
            {[
              { key: 'click', label: 'Clicks', tint: 'text-foreground' },
              { key: 'checkout_started', label: 'Checkout started', tint: 'text-sky-600' },
              { key: 'checkout_completed', label: 'Completed', tint: 'text-emerald-600' },
              { key: 'checkout_abandoned', label: 'Abandoned', tint: 'text-amber-600' },
              { key: 'checkout_failed', label: 'Failed', tint: 'text-rose-600' },
            ].map((k) => (
              <div key={k.key} className="bg-card p-4">
                <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
                  {k.label}
                </p>
                <p className={`mt-1 font-display text-2xl font-semibold tabular-nums ${k.tint}`}>
                  {payFunnel.counts?.[k.key] || 0}
                </p>
              </div>
            ))}
          </div>

          {payFunnel.items.length === 0 && !loadingPF ? (
            <div className="p-8 text-center text-sm text-muted-foreground border-t border-border">
              No payment events yet. As soon as visitors click any &ldquo;Buy&rdquo; or &ldquo;Upgrade&rdquo; CTA, events will start appearing here.
            </div>
          ) : (
            <div className="max-h-[440px] overflow-auto border-t border-border">
              <Table className="min-w-[1320px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>User / email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Page</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Razorpay id</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payFunnel.items.map((row) => {
                    const badgeClass =
                      row.event_type === 'checkout_completed'
                        ? 'bg-emerald-600 hover:bg-emerald-600 text-white'
                        : row.event_type === 'checkout_abandoned'
                        ? 'bg-amber-500 hover:bg-amber-500 text-white'
                        : row.event_type === 'checkout_failed'
                        ? 'bg-rose-600 hover:bg-rose-600 text-white'
                        : row.event_type === 'checkout_started'
                        ? 'bg-sky-600 hover:bg-sky-600 text-white'
                        : 'bg-secondary text-foreground';
                    return (
                      <TableRow key={row.id} data-testid={`admin-payment-event-row-${row.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={`uppercase text-[10px] tracking-wider ${badgeClass}`}>
                            {row.event_type.replace('checkout_', '')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">
                          {row.plan_type || '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap max-w-[220px] truncate" title={row.user_name || ''}>
                          {row.user_name ? (
                            <span className="font-medium">{row.user_name}</span>
                          ) : (
                            <span className="text-muted-foreground italic">
                              {row.is_guest ? 'guest · unknown' : '—'}
                            </span>
                          )}
                          {row.user_role && row.user_role !== 'viewer' && (
                            <RoleBadge role={row.user_role} size="xs" className="ml-1.5" />
                          )}
                          {row.user_country && (
                            <span className="ml-1.5 text-[10px] font-mono-edge uppercase tracking-[0.14em] text-muted-foreground">
                              {row.user_country}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.user_email ? (
                            <a href={`mailto:${row.user_email}`} className="text-primary hover:underline">
                              {row.user_email}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">
                              {row.user_id ? row.user_id.substring(0, 8) + '…' : 'anon'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono whitespace-nowrap">
                          {row.user_phone ? (
                            <a href={`tel:${row.user_phone}`} className="text-primary hover:underline">
                              {row.user_phone}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[11px] text-muted-foreground truncate max-w-[180px]" title={row.source_path}>
                          {row.source_path || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate" title={row.reason}>
                          {row.reason || '—'}
                        </TableCell>
                        <TableCell className="text-xs tabular-nums whitespace-nowrap">
                          {row.amount_paise
                            ? `${row.currency === 'USD' ? '$' : '₹'}${(row.amount_paise / 100).toLocaleString(row.currency === 'USD' ? 'en-US' : 'en-IN')}`
                            : '—'}
                        </TableCell>
                        <TableCell className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                          {row.razorpay_payment_id || row.razorpay_order_id || row.razorpay_subscription_id || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registered users — every account with signup context + admin actions */}
      <Card className="border-border mt-6" data-testid="admin-registered-users-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
            <Users size={18} className="text-primary" />
            <h2 className="font-semibold">Registered users</h2>
            <Badge variant="secondary" className="ml-1" data-testid="admin-registered-users-count">
              {regUsers.length}
            </Badge>
            <span className="text-xs text-muted-foreground hidden md:inline">
              Every account. Signup IP + full plan context + inline admin actions.
            </span>
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={regUsersQ}
                  onChange={(e) => setRegUsersQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') loadRegisteredUsers(regUsersQ);
                  }}
                  placeholder="Search email / name / id…"
                  className="pl-7 h-8 text-xs w-[220px]"
                  data-testid="admin-registered-users-search-input"
                />
              </div>
              <Button
                variant="default"
                size="sm"
                className="h-8"
                onClick={() => setGrantCompOpen(true)}
                data-testid="admin-grant-comp-open"
              >
                <Gift size={14} className="mr-1.5" />
                Grant complimentary
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => loadRegisteredUsers(regUsersQ)}
                disabled={loadingRU}
                data-testid="admin-registered-users-refresh"
              >
                {loadingRU ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </Button>
            </div>
          </div>

          {regUsers.length === 0 && !loadingRU ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {regUsersQ ? `No users match “${regUsersQ}”.` : 'No registered users.'}
            </div>
          ) : (
            <div className="max-h-[560px] overflow-auto">
              <Table className="min-w-[1280px]">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Signup IP</TableHead>
                    <TableHead>Referral</TableHead>
                    <TableHead>Signed up</TableHead>
                    <TableHead>Analyses</TableHead>
                    <TableHead className="w-[110px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regUsers.map((row) => (
                    <TableRow key={row.user_id} data-testid={`admin-registered-user-row-${row.user_id}`}>
                      <TableCell className="font-medium whitespace-nowrap">
                        {row.name || '—'}
                        {row.role && row.role !== 'viewer' && (
                          <RoleBadge role={row.role} className="ml-2" />
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <a href={`mailto:${row.email}`} className="text-primary hover:underline">
                          {row.email}
                        </a>
                        {row.must_reset_password && (
                          <Badge variant="outline" className="ml-2 text-[10px] text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-500/10">
                            Reset pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.plan_type ? (
                          row.is_active ? (
                            <Badge className="uppercase text-[10px] tracking-wider bg-emerald-600 hover:bg-emerald-600">
                              {row.plan_type === 'comp' || row.comp ? 'comp' : row.plan_type}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="uppercase text-[10px] tracking-wider text-muted-foreground border-muted">
                              {row.plan_type} · {row.status || 'none'}
                            </Badge>
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">No plan</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                        {row.signup_ip || '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.referral_path || row.referral_utm ? (
                          <div className="flex flex-col gap-0.5">
                            {row.referral_path && (
                              <span className="font-mono text-[11px] text-foreground truncate max-w-[180px]" title={row.referral_path}>
                                {row.referral_path}
                              </span>
                            )}
                            {row.referral_utm && (
                              <span className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={row.referral_utm}>
                                utm: {row.referral_utm}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.created_at)}</TableCell>
                      <TableCell className="text-xs tabular-nums text-center">
                        {row.analyses_count || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={actionUserId === row.user_id}
                              data-testid={`admin-registered-user-actions-${row.user_id}`}
                              aria-label="User actions"
                            >
                              {actionUserId === row.user_id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <MoreHorizontal size={14} />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                              Plan
                            </DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => runUserAction(row.user_id, 'activate_one_time')}>
                              <ArrowUpCircle size={14} className="mr-2" />
                              Activate Profile Success (15d)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => runUserAction(row.user_id, 'activate_gold')}>
                              <ArrowUpCircle size={14} className="mr-2" />
                              Activate Executive (30d)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => runUserAction(row.user_id, 'activate_comp')}>
                              <CheckCircle2 size={14} className="mr-2" />
                              Grant complimentary
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => runUserAction(row.user_id, 'cancel')}
                              className="text-destructive focus:text-destructive"
                            >
                              <XCircle size={14} className="mr-2" />
                              Cancel membership
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                              Account
                            </DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => runUserAction(row.user_id, 'reset_password')}>
                              <KeyRound size={14} className="mr-2" />
                              Reset password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => runUserAction(row.user_id, 'send_invoice')}
                              disabled={!row.plan_type || row.plan_type === 'none'}
                            >
                              <Receipt size={14} className="mr-2" />
                              Send invoice / receipt
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Razorpay payments — fetched directly from Razorpay */}
      <Card className="border-border mt-6" data-testid="admin-rzp-payments-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
            <ExternalLink size={18} className="text-primary" />
            <h2 className="font-semibold">Recent Razorpay payments (live)</h2>
            <Badge variant="secondary" className="ml-1" data-testid="admin-rzp-payments-count">
              {rzpPayments.length}
            </Badge>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Fetched directly from Razorpay API. Cross-referenced with local users.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadRzpPayments}
              disabled={loadingRZP}
              className="ml-auto"
              data-testid="admin-rzp-payments-refresh"
            >
              {loadingRZP ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
          </div>

          {rzpPayments.length === 0 && !loadingRZP ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No recent payments returned by Razorpay.
            </div>
          ) : (
            <div className="max-h-[440px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead>Paid at</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment email</TableHead>
                    <TableHead>Match</TableHead>
                    <TableHead>Local plan</TableHead>
                    <TableHead>Payment ID</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rzpPayments.map((row) => {
                    const inr = (row.amount_paise || 0) / 100;
                    const matched = row.matches_by_notes || row.matches_by_email;
                    const orphan = row.status === 'captured' && !row.local_plan_active;
                    return (
                      <TableRow key={row.payment_id} className={orphan ? 'bg-amber-50/60 dark:bg-amber-500/5' : ''}>
                        <TableCell className="whitespace-nowrap text-xs">
                          {fmtEpoch(row.captured_at)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={row.status === 'captured' ? 'default' : 'secondary'}
                            className="uppercase text-[10px] tracking-wider"
                          >
                            {row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="tabular-nums text-xs">
                          {row.currency === 'INR' ? '₹' : row.currency + ' '}
                          {inr.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.email ? (
                            <a href={`mailto:${row.email}`} className="text-primary hover:underline">
                              {row.email}
                            </a>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {matched ? (
                            <div className="text-[11px]">
                              <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                                <CheckCircle2 size={11} /> Matched
                              </div>
                              <div className="text-muted-foreground">{row.matched_user_email}</div>
                              <div className="font-mono text-[10px] text-muted-foreground">
                                {row.matched_user_id?.substring(0, 8)}…
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                              <AlertTriangle size={11} /> Unmatched
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.local_plan_active ? (
                            <Badge className="text-[10px]">Active</Badge>
                          ) : row.status === 'captured' ? (
                            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-500/10">
                              Not activated
                            </Badge>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                          {row.payment_id}
                        </TableCell>
                        <TableCell className="min-w-[220px]">
                          {row.status === 'captured' && (
                            <div className="flex gap-1 items-center">
                              <Input
                                placeholder="target user id"
                                value={attachUidByPid[row.payment_id] || ''}
                                onChange={(e) =>
                                  setAttachUidByPid((prev) => ({
                                    ...prev,
                                    [row.payment_id]: e.target.value,
                                  }))
                                }
                                className="h-7 text-[11px] font-mono"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-[11px]"
                                disabled={attachingId === row.payment_id}
                                onClick={() =>
                                  attachPaymentTo(row.payment_id, (attachUidByPid[row.payment_id] || '').trim())
                                }
                              >
                                {attachingId === row.payment_id ? (
                                  <Loader2 size={11} className="animate-spin" />
                                ) : (
                                  <><Link2 size={11} className="mr-1" /> Attach</>
                                )}
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enterprise + Quarterly enquiries — tabbed CRM inbox */}
      <EnterpriseEnquiriesInbox />
    </>
  );
};

export default BillingProvisionsPanel;
